import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import workflowService from '../../services/workflowService';

// Initial state
const initialState = {
  // Approvals data
  pendingApprovals: [],
  allApprovals: [],
  approvalHistory: [],
  currentApproval: null,

  // Workflow instances
  workflowInstances: [],
  currentWorkflowInstance: null,
  workflowTimeline: null,
  workflowAuditTrail: [],
  workflowDocument: null,

  // Notifications
  notifications: [],
  unreadCount: 0,

  // UI state
  isLoading: false,
  isActionLoading: false, // For approve/reject/delegate actions
  isDocumentLoading: false, // For the source-document preview
  documentError: null,
  error: null,
  actionError: null,
  successMessage: null,

  // Pagination
  totalCount: 0,
  currentPage: 1,
  pageSize: 50,
};

// ==================== Async Thunks ====================

/**
 * Fetch pending approvals for current user
 */
export const fetchPendingApprovals = createAsyncThunk(
  'workflowApproval/fetchPendingApprovals',
  async (_, { rejectWithValue }) => {
    try {
      console.log('fetchPendingApprovals: Making API call to /workflow/api/approvals/pending/');
      const response = await workflowService.getPendingApprovals();
      console.log('fetchPendingApprovals: Success, received data:', response.data);
      return response.data;
    } catch (error) {
      console.error('fetchPendingApprovals: Error occurred:', error);
      console.error('fetchPendingApprovals: Error response:', error.response);
      const errorData = error.response?.data || { message: 'Failed to fetch pending approvals' };
      console.error('fetchPendingApprovals: Rejecting with:', errorData);
      return rejectWithValue(errorData);
    }
  }
);

/**
 * Fetch all approvals with optional filters
 */
export const fetchAllApprovals = createAsyncThunk(
  'workflowApproval/fetchAllApprovals',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await workflowService.getApprovals(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to fetch approvals' });
    }
  }
);

/**
 * Fetch approval history (approved/rejected by current user)
 */
export const fetchApprovalHistory = createAsyncThunk(
  'workflowApproval/fetchApprovalHistory',
  async (_, { rejectWithValue }) => {
    try {
      // Fetch approvals with action filter (approved, rejected, returned)
      const response = await workflowService.getApprovals({
        action__in: 'approved,rejected,returned'
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to fetch approval history' });
    }
  }
);

/**
 * Fetch single approval detail
 */
export const fetchApprovalDetail = createAsyncThunk(
  'workflowApproval/fetchApprovalDetail',
  async (approvalId, { rejectWithValue }) => {
    try {
      const response = await workflowService.getApproval(approvalId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to fetch approval detail' });
    }
  }
);

/**
 * Approve a workflow
 */
export const approveWorkflow = createAsyncThunk(
  'workflowApproval/approveWorkflow',
  async ({ approvalId, comments }, { rejectWithValue }) => {
    try {
      const response = await workflowService.approveWorkflow(approvalId, comments);
      return { approvalId, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to approve workflow' });
    }
  }
);

/**
 * Reject a workflow
 */
export const rejectWorkflow = createAsyncThunk(
  'workflowApproval/rejectWorkflow',
  async ({ approvalId, comments }, { rejectWithValue }) => {
    try {
      const response = await workflowService.rejectWorkflow(approvalId, comments);
      return { approvalId, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to reject workflow' });
    }
  }
);

/**
 * Return workflow for revision
 */
export const returnForRevision = createAsyncThunk(
  'workflowApproval/returnForRevision',
  async ({ approvalId, comments }, { rejectWithValue }) => {
    try {
      const response = await workflowService.returnForRevision(approvalId, comments);
      return { approvalId, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to return workflow for revision' });
    }
  }
);

/**
 * Delegate approval to another user
 */
export const delegateApproval = createAsyncThunk(
  'workflowApproval/delegateApproval',
  async ({ approvalId, delegateToUserId, comments }, { rejectWithValue }) => {
    try {
      const response = await workflowService.delegateApproval(approvalId, delegateToUserId, comments);
      return { approvalId, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to delegate approval' });
    }
  }
);

/**
 * Fetch workflow instance detail
 */
export const fetchWorkflowInstance = createAsyncThunk(
  'workflowApproval/fetchWorkflowInstance',
  async (instanceId, { rejectWithValue }) => {
    try {
      const response = await workflowService.getWorkflowInstance(instanceId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to fetch workflow instance' });
    }
  }
);

/**
 * Fetch workflow timeline
 */
export const fetchWorkflowTimeline = createAsyncThunk(
  'workflowApproval/fetchWorkflowTimeline',
  async (instanceId, { rejectWithValue }) => {
    try {
      const response = await workflowService.getWorkflowTimeline(instanceId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to fetch workflow timeline' });
    }
  }
);

/**
 * Fetch workflow audit trail
 */
export const fetchWorkflowAuditTrail = createAsyncThunk(
  'workflowApproval/fetchWorkflowAuditTrail',
  async (instanceId, { rejectWithValue }) => {
    try {
      const response = await workflowService.getWorkflowAuditTrail(instanceId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to fetch audit trail' });
    }
  }
);

/**
 * Fetch the source document that triggered the workflow
 */
export const fetchWorkflowDocument = createAsyncThunk(
  'workflowApproval/fetchWorkflowDocument',
  async (instanceId, { rejectWithValue }) => {
    try {
      const response = await workflowService.getWorkflowDocument(instanceId);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: 'Failed to load the source document' }
      );
    }
  }
);

/**
 * Fetch notifications
 */
export const fetchNotifications = createAsyncThunk(
  'workflowApproval/fetchNotifications',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await workflowService.getNotifications(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to fetch notifications' });
    }
  }
);

/**
 * Fetch unread notification count
 */
export const fetchUnreadNotificationCount = createAsyncThunk(
  'workflowApproval/fetchUnreadNotificationCount',
  async (_, { rejectWithValue }) => {
    try {
      const response = await workflowService.getUnreadNotificationCount();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to fetch unread count' });
    }
  }
);

/**
 * Mark notifications as read
 */
export const markNotificationsRead = createAsyncThunk(
  'workflowApproval/markNotificationsRead',
  async ({ notificationIds = [], markAll = false }, { rejectWithValue }) => {
    try {
      const response = await workflowService.markNotificationsRead(notificationIds, markAll);
      return { notificationIds, markAll, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to mark notifications as read' });
    }
  }
);

// ==================== Slice ====================

const workflowApprovalSlice = createSlice({
  name: 'workflowApproval',
  initialState,
  reducers: {
    // Clear current approval
    clearCurrentApproval: (state) => {
      state.currentApproval = null;
    },

    // Clear current workflow instance
    clearCurrentWorkflowInstance: (state) => {
      state.currentWorkflowInstance = null;
      state.workflowTimeline = null;
      state.workflowAuditTrail = [];
      state.workflowDocument = null;
    },

    // Clear error messages
    clearError: (state) => {
      state.error = null;
      state.actionError = null;
      state.documentError = null;
    },

    // Clear success message
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },

    // Set success message
    setSuccessMessage: (state, action) => {
      state.successMessage = action.payload;
    },

    // Remove approval from pending list (after action taken)
    removeFromPendingApprovals: (state, action) => {
      state.pendingApprovals = state.pendingApprovals.filter(
        (approval) => approval.id !== action.payload
      );
    },

    // Real-time: prepend a new notification from WebSocket
    prependNotification: (state, action) => {
      state.notifications = [action.payload, ...state.notifications];
      state.unreadCount += 1;
    },

    /**
     * Real-time: add a notification, or REPLACE the one already standing for
     * the same thing.
     *
     * Project Chat coalesces server-side — one unread row per conversation,
     * updated in place — so a plain prepend would show N entries for a row
     * that exists once in the database, and a badge N times too high that
     * silently corrects itself on the next reload.
     *
     * `is_new` comes from the server because the client cannot work it out:
     * only the writer knows whether it inserted or updated. Absent, we fall
     * back to "did we already have this row?", which is right for anything
     * that does not coalesce.
     */
    upsertNotification: (state, action) => {
      const incoming = action.payload;
      const key = (n) => n.id ?? n.action_url;
      const index = state.notifications.findIndex(
        (n) => key(n) === key(incoming)
          || (n.action_url && n.action_url === incoming.action_url && !n.is_read)
      );

      if (index >= 0) {
        // Move it to the top: the bell is ordered newest-first and this
        // conversation has just spoken.
        state.notifications.splice(index, 1);
      }
      state.notifications = [incoming, ...state.notifications];

      const isNew = incoming.is_new ?? (index < 0);
      if (isNew) state.unreadCount += 1;
    },

    // Real-time: set unread count from WebSocket data
    setUnreadCount: (state, action) => {
      state.unreadCount = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // ==================== Fetch Pending Approvals ====================
      .addCase(fetchPendingApprovals.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPendingApprovals.fulfilled, (state, action) => {
        state.isLoading = false;
        state.pendingApprovals = action.payload.results || action.payload;
        state.totalCount = action.payload.count || action.payload.length;
      })
      .addCase(fetchPendingApprovals.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // ==================== Fetch All Approvals ====================
      .addCase(fetchAllApprovals.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllApprovals.fulfilled, (state, action) => {
        state.isLoading = false;
        state.allApprovals = action.payload.results || action.payload;
        state.totalCount = action.payload.count || action.payload.length;
      })
      .addCase(fetchAllApprovals.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // ==================== Fetch Approval History ====================
      .addCase(fetchApprovalHistory.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchApprovalHistory.fulfilled, (state, action) => {
        state.isLoading = false;
        state.approvalHistory = action.payload.results || action.payload;
      })
      .addCase(fetchApprovalHistory.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // ==================== Fetch Approval Detail ====================
      .addCase(fetchApprovalDetail.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchApprovalDetail.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentApproval = action.payload;
      })
      .addCase(fetchApprovalDetail.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // ==================== Approve Workflow ====================
      .addCase(approveWorkflow.pending, (state) => {
        state.isActionLoading = true;
        state.actionError = null;
      })
      .addCase(approveWorkflow.fulfilled, (state, action) => {
        state.isActionLoading = false;
        state.successMessage = 'Workflow approved successfully';
        // Remove from pending approvals
        state.pendingApprovals = state.pendingApprovals.filter(
          (approval) => approval.id !== action.payload.approvalId
        );
      })
      .addCase(approveWorkflow.rejected, (state, action) => {
        state.isActionLoading = false;
        state.actionError = action.payload;
      })

      // ==================== Reject Workflow ====================
      .addCase(rejectWorkflow.pending, (state) => {
        state.isActionLoading = true;
        state.actionError = null;
      })
      .addCase(rejectWorkflow.fulfilled, (state, action) => {
        state.isActionLoading = false;
        state.successMessage = 'Workflow rejected successfully';
        // Remove from pending approvals
        state.pendingApprovals = state.pendingApprovals.filter(
          (approval) => approval.id !== action.payload.approvalId
        );
      })
      .addCase(rejectWorkflow.rejected, (state, action) => {
        state.isActionLoading = false;
        state.actionError = action.payload;
      })

      // ==================== Return for Revision ====================
      .addCase(returnForRevision.pending, (state) => {
        state.isActionLoading = true;
        state.actionError = null;
      })
      .addCase(returnForRevision.fulfilled, (state, action) => {
        state.isActionLoading = false;
        state.successMessage = 'Workflow returned for revision';
        // Remove from pending approvals
        state.pendingApprovals = state.pendingApprovals.filter(
          (approval) => approval.id !== action.payload.approvalId
        );
      })
      .addCase(returnForRevision.rejected, (state, action) => {
        state.isActionLoading = false;
        state.actionError = action.payload;
      })

      // ==================== Delegate Approval ====================
      .addCase(delegateApproval.pending, (state) => {
        state.isActionLoading = true;
        state.actionError = null;
      })
      .addCase(delegateApproval.fulfilled, (state, action) => {
        state.isActionLoading = false;
        state.successMessage = 'Approval delegated successfully';
        // Remove from pending approvals
        state.pendingApprovals = state.pendingApprovals.filter(
          (approval) => approval.id !== action.payload.approvalId
        );
      })
      .addCase(delegateApproval.rejected, (state, action) => {
        state.isActionLoading = false;
        state.actionError = action.payload;
      })

      // ==================== Fetch Workflow Instance ====================
      .addCase(fetchWorkflowInstance.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchWorkflowInstance.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentWorkflowInstance = action.payload;
      })
      .addCase(fetchWorkflowInstance.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // ==================== Fetch Workflow Timeline ====================
      .addCase(fetchWorkflowTimeline.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchWorkflowTimeline.fulfilled, (state, action) => {
        state.isLoading = false;
        state.workflowTimeline = action.payload;
      })
      .addCase(fetchWorkflowTimeline.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // ==================== Fetch Workflow Document ====================
      .addCase(fetchWorkflowDocument.pending, (state) => {
        state.isDocumentLoading = true;
        state.documentError = null;
      })
      .addCase(fetchWorkflowDocument.fulfilled, (state, action) => {
        state.isDocumentLoading = false;
        state.workflowDocument = action.payload;
      })
      .addCase(fetchWorkflowDocument.rejected, (state, action) => {
        state.isDocumentLoading = false;
        state.workflowDocument = null;
        state.documentError = action.payload;
      })

      // ==================== Fetch Workflow Audit Trail ====================
      .addCase(fetchWorkflowAuditTrail.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchWorkflowAuditTrail.fulfilled, (state, action) => {
        state.isLoading = false;
        state.workflowAuditTrail = action.payload.results || action.payload;
      })
      .addCase(fetchWorkflowAuditTrail.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // ==================== Fetch Notifications ====================
      .addCase(fetchNotifications.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.isLoading = false;
        state.notifications = action.payload.results || action.payload;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // ==================== Fetch Unread Notification Count ====================
      .addCase(fetchUnreadNotificationCount.fulfilled, (state, action) => {
        state.unreadCount = action.payload.count || 0;
      })

      // ==================== Mark Notifications Read ====================
      .addCase(markNotificationsRead.fulfilled, (state, action) => {
        if (action.payload.markAll) {
          // Mark all notifications as read
          state.notifications = state.notifications.map(notif => ({
            ...notif,
            is_read: true
          }));
          state.unreadCount = 0;
        } else {
          // Mark specific notifications as read
          const readIds = action.payload.notificationIds;
          state.notifications = state.notifications.map(notif =>
            readIds.includes(notif.id) ? { ...notif, is_read: true } : notif
          );
          state.unreadCount = Math.max(0, state.unreadCount - readIds.length);
        }
      });
  },
});

// Export actions
export const {
  clearCurrentApproval,
  clearCurrentWorkflowInstance,
  clearError,
  clearSuccessMessage,
  setSuccessMessage,
  removeFromPendingApprovals,
  prependNotification,
  upsertNotification,
  setUnreadCount,
} = workflowApprovalSlice.actions;

// Export selectors
export const selectPendingApprovals = (state) => state.workflowApproval.pendingApprovals;
export const selectAllApprovals = (state) => state.workflowApproval.allApprovals;
export const selectApprovalHistory = (state) => state.workflowApproval.approvalHistory;
export const selectCurrentApproval = (state) => state.workflowApproval.currentApproval;
export const selectCurrentWorkflowInstance = (state) => state.workflowApproval.currentWorkflowInstance;
export const selectWorkflowTimeline = (state) => state.workflowApproval.workflowTimeline;
export const selectWorkflowAuditTrail = (state) => state.workflowApproval.workflowAuditTrail;
export const selectWorkflowDocument = (state) => state.workflowApproval.workflowDocument;
export const selectIsDocumentLoading = (state) => state.workflowApproval.isDocumentLoading;
export const selectDocumentError = (state) => state.workflowApproval.documentError;
export const selectNotifications = (state) => state.workflowApproval.notifications;
export const selectUnreadCount = (state) => state.workflowApproval.unreadCount;
export const selectIsLoading = (state) => state.workflowApproval.isLoading;
export const selectIsActionLoading = (state) => state.workflowApproval.isActionLoading;
export const selectError = (state) => state.workflowApproval.error;
export const selectActionError = (state) => state.workflowApproval.actionError;
export const selectSuccessMessage = (state) => state.workflowApproval.successMessage;

// Export reducer
export default workflowApprovalSlice.reducer;
